import type { FC, MouseEvent as ReactMouseEvent, ReactNode } from 'react'
import { useSelector } from 'react-redux'

import DialogNames from '~/constants/DialogNames'
import { getLabOrderStatuses } from '~/store/reducers/constants'
import useDialog from '~/utils/useDialog'

import { AgentLink } from './AgentLink'
import {
  getOrderResultsDialogParams,
  parseLabTestDashboardUrl,
} from './labTestLinkUtils'

export interface LabTestLinkButtonProps {
  [key: string]: any
  children?: ReactNode
  className?: string
  href: string
  onLinkClick?: () => void
}

/**
 * Lab test link button: opens OrderResultsDialog when URL has all required params,
 * otherwise opens lab tests dashboard in a new tab.
 */
export const LabTestLinkButton: FC<LabTestLinkButtonProps> = ({
  href,
  children,
  className,
  onLinkClick,
  ...domProps
}) => {
  // Commented out as a part of CVC-35590. Will be re-introduced in the future when we use a button again.
  // const { t } = useTranslation('Common')
  const labOrderStatuses = useSelector(getLabOrderStatuses)
  const [openOrderResultsDialog] = useDialog(DialogNames.ORDER_RESULTS)

  const parsed = parseLabTestDashboardUrl(href)
  const dialogParams =
    parsed &&
    getOrderResultsDialogParams(
      parsed.labTestIdentifier,
      parsed.queryParams,
      labOrderStatuses,
    )

  const handleClick = (e: ReactMouseEvent) => {
    e.preventDefault()
    onLinkClick?.()
    if (dialogParams) {
      openOrderResultsDialog({
        canComplete: dialogParams.canComplete,
        hasResults: dialogParams.hasResults,
        orderId: dialogParams.orderId,
        pdfUrl: dialogParams.pdfUrl,
        vendorId: dialogParams.vendorId,
      })
    } else {
      window.open(href, '_blank')
    }
  }

  // Commented out as a part of CVC-35590. Will be re-introduced in the future when we use a button again.
  // const tooltipContent = t('Common:VIEW_LAB_TEST_RESULTS')
  // const ariaLabel = getChildrenText(children) || tooltipContent

  return (
    <AgentLink
      className={className}
      href={href}
      {...domProps}
      onClick={handleClick}
    >
      {children}
    </AgentLink>
  )

  // Commented out as a part of CVC-35590. Will be re-introduced in the future when we use a button again.
  // return (
  //   <AgentChatLinkButton
  //     ariaLabel={ariaLabel}
  //     className={className}
  //     tooltipContent={tooltipContent}
  //     onClick={handleClick}
  //   />
  // )
}
